/**
 * Enhanced BaseDialog Component
 *
 * Extended version of BaseDialog with visual update indicators,
 * animations, and field change tracking capabilities.
 *
 * @author Claude Code
 * @date 2025-09-22
 */

import React, { useContext, useMemo } from 'react';
import { Box, Dialog, DialogTitle, DialogContent, DialogActions, Typography, Chip, Fade, Slide } from '@mui/material';
import { styled, keyframes } from '@mui/material/styles';
import { FieldUpdateContext } from '../../contexts/FieldUpdateContext';
import { FieldUpdateEvent, FieldUpdateContextValue } from '../../types';

// Animation keyframes
const pulseGlow = keyframes`
  0% {
    box-shadow: 0 0 0 0 rgba(25, 118, 210, 0.4);
  }
  70% {
    box-shadow: 0 0 0 4px rgba(25, 118, 210, 0);
  }
  100% {
    box-shadow: 0 0 0 0 rgba(25, 118, 210, 0);
  }
`;

const slideInFromRight = keyframes`
  from {
    transform: translateX(100%);
    opacity: 0;
  }
  to {
    transform: translateX(0);
    opacity: 1;
  }
`;

// Styled components
const UpdateIndicatorContainer = styled(Box)(({ theme }) => ({
  position: 'absolute',
  top: theme.spacing(1),
  right: theme.spacing(1),
  zIndex: 1300,
  display: 'flex',
  flexDirection: 'column',
  gap: theme.spacing(0.5),
  maxWidth: '200px'
}));

const UpdateChip = styled(Chip, {
  shouldForwardProp: (prop) => prop !== 'updateSource'
})<{ updateSource: 'firebase' | 'user' | 'system' | 'ai' }>(({ theme, updateSource }) => ({
  animation: `${slideInFromRight} 0.3s ease-out`,
  fontSize: '0.75rem',
  height: '24px',
  backgroundColor: updateSource === 'firebase'
    ? theme.palette.info.light
    : updateSource === 'user'
    ? theme.palette.success.light
    : updateSource === 'ai'
    ? theme.palette.secondary.light
    : theme.palette.warning.light,
  color: theme.palette.getContrastText(
    updateSource === 'firebase'
      ? theme.palette.info.light
      : updateSource === 'user'
      ? theme.palette.success.light
      : updateSource === 'ai'
      ? theme.palette.secondary.light
      : theme.palette.warning.light
  ),
  '& .MuiChip-deleteIcon': {
    fontSize: '16px'
  }
}));

const AnimatedDialog = styled(Dialog, {
  shouldForwardProp: (prop) => prop !== 'hasUpdates'
})<{ hasUpdates: boolean }>(({ theme, hasUpdates }) => ({
  '& .MuiDialog-paper': {
    position: 'relative',
    overflow: 'visible',
    animation: hasUpdates ? `${pulseGlow} 2s infinite` : 'none',
    transition: 'box-shadow 0.3s ease-in-out',
  }
}));

const UpdatedSectionIndicator = styled(Box)(({ theme }) => ({
  position: 'absolute',
  left: '-4px',
  top: 0,
  bottom: 0,
  width: '4px',
  backgroundColor: theme.palette.primary.main,
  borderRadius: '2px',
  animation: `${pulseGlow} 1.5s ease-in-out 3`,
}));

export interface BaseDialogEnhancedProps {
  open: boolean;
  onClose: () => void;
  title: string;
  children: React.ReactNode;
  maxWidth?: 'xs' | 'sm' | 'md' | 'lg' | 'xl';
  fullWidth?: boolean;
  actions?: React.ReactNode;
  showUpdateIndicators?: boolean;
  onFieldUpdate?: (event: FieldUpdateEvent) => void;
  className?: string;
}

/**
 * Enhanced BaseDialog with update tracking and visual indicators
 */
export const BaseDialogEnhanced: React.FC<BaseDialogEnhancedProps> = ({
  open,
  onClose,
  title,
  children,
  maxWidth = 'md',
  fullWidth = true,
  actions,
  showUpdateIndicators = true,
  onFieldUpdate,
  className
}) => {
  const fieldUpdateContext = useContext(FieldUpdateContext) as FieldUpdateContextValue | null;

  // Get recent updates for this dialog
  const recentUpdates = useMemo(() => {
    if (!fieldUpdateContext || !showUpdateIndicators) {
      return [];
    }

    const now = Date.now();
    const fiveMinutesAgo = now - (5 * 60 * 1000);

    return fieldUpdateContext.fieldUpdates
      .filter(update => update.timestamp > fiveMinutesAgo)
      .slice(0, 5) // Show max 5 recent updates
      .sort((a, b) => b.timestamp - a.timestamp);
  }, [fieldUpdateContext?.fieldUpdates, showUpdateIndicators]);

  // Check if there are any recent updates
  const hasRecentUpdates = recentUpdates.length > 0;

  // Format field path for display
  const formatFieldPath = (path: string): string => {
    if (!path || typeof path !== 'string') {
      return 'unknown field';
    }

    return path
      .split('.')
      .map(part => {
        // Convert camelCase to readable format
        if (part.match(/^[0-9]+$/)) {
          return `#${parseInt(part) + 1}`;
        }
        return part.replace(/([A-Z])/g, ' $1').toLowerCase();
      })
      .join(' → ');
  };

  // Get source icon
  const getSourceIcon = (source: string) => {
    switch (source) {
      case 'firebase': return '🔄';
      case 'user': return '👤';
      case 'system': return '⚙️';
      default: return '📝';
    }
  };

  // Handle update chip deletion
  const handleUpdateDismiss = (updateId: string) => {
    if (fieldUpdateContext?.dismissUpdate) {
      fieldUpdateContext.dismissUpdate(updateId);
    }
  };

  return (
    <AnimatedDialog
      open={open}
      onClose={onClose}
      maxWidth={maxWidth}
      fullWidth={fullWidth}
      hasUpdates={hasRecentUpdates}
      className={className}
    >
      {/* Update indicators */}
      {showUpdateIndicators && recentUpdates.length > 0 && (
        <UpdateIndicatorContainer>
          {recentUpdates.map((update) => (
            <Fade key={update.id} in timeout={300}>
              <UpdateChip
                updateSource={update.source}
                size="small"
                label={
                  <Box display="flex" alignItems="center" gap={0.5}>
                    <span>{getSourceIcon(update.source)}</span>
                    <Typography variant="caption" noWrap>
                      {formatFieldPath(update.path)}
                    </Typography>
                  </Box>
                }
                onDelete={() => handleUpdateDismiss(update.id)}
                deleteIcon={<span>×</span>}
              />
            </Fade>
          ))}
        </UpdateIndicatorContainer>
      )}

      <DialogTitle>
        <Box display="flex" alignItems="center" justifyContent="space-between">
          <Typography variant="h6" component="div">
            {title}
          </Typography>
          {hasRecentUpdates && (
            <Chip
              size="small"
              label={`${recentUpdates.length} обновлений`}
              color="primary"
              variant="outlined"
            />
          )}
        </Box>
      </DialogTitle>

      <DialogContent>
        <Box position="relative">
          {children}
        </Box>
      </DialogContent>

      {actions && (
        <DialogActions>
          {actions}
        </DialogActions>
      )}
    </AnimatedDialog>
  );
};

/**
 * Higher-order component to wrap sections with update indicators
 */
export const withUpdateIndicator = <P extends object>(
  Component: React.ComponentType<P>,
  sectionPath: string
) => {
  return React.forwardRef<any, P>((props, ref) => {
    const fieldUpdateContext = useContext(FieldUpdateContext) as FieldUpdateContextValue | null;

    // Check if this section has recent updates
    const hasUpdates = useMemo(() => {
      if (!fieldUpdateContext) return false;

      const now = Date.now();
      const thirtySecondsAgo = now - (30 * 1000);

      return fieldUpdateContext.fieldUpdates.some(update =>
        update.path.startsWith(sectionPath) &&
        update.timestamp > thirtySecondsAgo
      );
    }, [fieldUpdateContext?.fieldUpdates, sectionPath]);

    return (
      <Box position="relative">
        {hasUpdates && (
          <Slide direction="right" in={hasUpdates} timeout={300}>
            <UpdatedSectionIndicator />
          </Slide>
        )}
        <Component {...(props as any)} ref={ref} />
      </Box>
    );
  });
};

export default BaseDialogEnhanced;